// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { getCollectDeliveries } from 'apis/collects';
import { Modal } from 'components/atoms/Modal';
import { useEffect, useMemo, useState } from 'react';
import { CollectDelivery } from 'util/Types';
import styles from './CollectDeliveriesDataModal.module.scss';
import { useTranslation } from 'react-i18next';
import { IconCloseTablet } from 'components/atoms/icons/IconCloseTablet';
import { mergeClasses, normalizeNumber } from 'util/commons';
import DOMPurify from 'dompurify';

type CollectDeliveriesDataModalProps = {
  collectId?: string;
  onClose?: () => void;
  title?: React.ReactNode;
};

const CollectDeliveriesDataModal: React.FC<CollectDeliveriesDataModalProps> = ({
  collectId,
  onClose,
  title,
}) => {
  const [data, setData] = useState<CollectDelivery[]>([]);
  const { t } = useTranslation();

  const getDeliveryData = () => {
    if (!collectId) {
      return;
    }

    getCollectDeliveries({
      deliveryIds: [collectId],
      cursor: null,
      successCallback: setData,
      failedCallback: () => {
        setData([]);
        onClose?.();
      },
    });
  };

  useEffect(() => {
    getDeliveryData();
  }, [collectId]);

  const totalWeight = useMemo(
    () => data.reduce((acc, { weight }) => acc + Number(weight), 0),
    [data]
  );

  if (!collectId) {
    return null;
  }

  const renderDataList = () => {
    if (!data.length) {
      return (
        <tr>
          <td colSpan={3} className={styles.noData}>
            {t('messages.M_008')}
          </td>
        </tr>
      );
    }

    return data.map((item, index) => (
      <tr key={index}>
        <td>{item.tenantName}</td>
        <td>{item.processingObjectId}</td>
        <td>
          {normalizeNumber({
            value: item.weight,
          })}
          kg
        </td>
      </tr>
    ));
  };

  return (
    <Modal centered className={styles.collectDeliveriesDataModal}>
      <div className={styles.title}>
        <p>{title || t('common.detail')}</p>
        <IconCloseTablet onClick={onClose} />
      </div>

      <div className={styles.body}>
        <div className={styles.overview}>
          <div
            className="count-list"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                t('common.page', {
                  page: data.length,
                }) ?? ''
              ),
            }}
          />

          <div>
            {t('collect_registration.total_weight', {
              weight: normalizeNumber({ value: totalWeight }),
            })}
          </div>
        </div>

        <table className={mergeClasses('primary-table', styles.table)}>
          <thead>
            <tr>
              <th>{t('collect_registration.tenant_name')}</th>
              <th>{t('histories.label_csv_emission_id')}</th>
              <th>{t('common_history_field.weight')}</th>
            </tr>
          </thead>

          <tbody>{renderDataList()}</tbody>
        </table>
      </div>
    </Modal>
  );
};

export default CollectDeliveriesDataModal;
